/**
 * Monogram rozeti — eksik kapak görselinin yerine geçen baş harf + renk.
 *
 * ## Neden (karar: "monogram rozeti orta yol olabilir")
 *
 * Kapak görseli olmayan içeriklerde bugün **hiçbir şey** gösteriliyordu:
 * `ItemCard` görsel yüklenemezse `<img>`'yi gizliyor ve geriye boş bir
 * degrade kutu kalıyordu. Kullanıcı içeriğin var olup olmadığını anlayamıyordu.
 *
 * Seçenekler ve neden bu:
 *   - **Boş kutu** → içerik yok gibi görünür. Yanlış bilgi verir. ✗
 *   - **Emoji ikon** (📺/🎬/🎵) → platforma göre farklı görünür, 100+ emoji
 *     kullanımının bir parçası; zaten kaldırılması planlanıyor. ✗
 *   - **Monogram (harf + renk)** → içeriğin adını taşır, platformdan bağımsız,
 *     ayrıca düzenli bir ızgara oluşturur (her kart aynı hizada durur). ✓
 *
 * Bu dosya **saf fonksiyonlar** içerir; renk seçimi kararlıdır (aynı öğe her
 * açılışta aynı rengi alır), böylece liste kaydırılırken kartlar "dans etmez".
 */

/** Baş harf için kullanılmayan bağlaçlar (yerelleştirilmiş birkaç dil). */
const LEADING_ARTICLES = new Set([
  'the',
  'a',
  'an',
  'le',
  'la',
  'les',
  'el',
  'los',
  'las',
  'il',
  'lo',
  'der',
  'die',
  'das',
  'de',
  'het',
]);

/**
 * Başlıktan 1–2 harflik monogram üretir.
 *
 * Kurallar:
 *   - Baştaki bağlaç atılır ("The Dropout" → **DR**, "A Quiet Place" → **QP**).
 *   - İki kelimelik başlıkta ilk iki kelimenin **ilk harfi** ("Dark Matter" → DM).
 *   - Tek kelimelik başlıkta ilk iki harf ("Silo" → SI, "1917" → 19).
 *   - **Rakamdan harf üretilmez.** Müzik katalogları numarayla başlar:
 *     "Aydilge - 04 - Yalnızlıkla Yaptım" başlığında ikinci kelime "04" idi ve
 *     monogram **"A0"** çıkıyordu — ekranda görüldüğü gibi anlamsız. Yerine
 *     ilk kelimenin ilk iki harfi kullanılır (**AY**).
 *   - Harf/rakam dışı karakterler temizlenir; hiçbir harf kalmazsa `?`.
 *   - Uzunluğu sabit tutar: kartlarda metin hizası bozulmasın.
 *
 * ## "1917" gibi başlıklar neden bozulmadı?
 *
 * Kural **ikinci** konuma bakıyor, birincisine değil. "1917" tek kelimedir:
 * ikinci konum yok, ilk iki karakter kullanılır → 19. "2001 A Space Odyssey"
 * ise ikinci kelime harf içeriyor → 2A korunur. Yalnızca *rakam* ikinci
 * konuma düştüğünde devreye girer; klasik yapım adları etkilenmez.
 *
 * ## Neden `toUpperCase()` ve `toLocaleUpperCase('tr-TR')` değil?
 *
 * Türkçe yerel büyütmede noktalı/noktasız `i` ayrışır: "Silo" → **"Sİlo"**.
 * Dilbilgisel olarak doğru, ama monogramda **yanlış** okunuyor — kullanıcı
 * kartın "Silo" olduğunu değil "Sİlo" olduğunu düşünür. Yerel bağımsız
 * `toUpperCase()` İngilizce kurallarını uygular (`i` → `I`) ve Türkçe
 * harfleri (`Ç Ğ İ Ö Ş Ü`) olduğu gibi korur.
 */
/*
 * Eski Tizen (Chromium 34) Unicode property escape (`\p{L}` / `\p{N}`)
 * desteklemez; regex motoru paketi parse ederken "Invalid escape" fırlatır.
 * Latin-1 + Latin Extended-A/B aralıkları Türkçe karakterleri kapsar.
 */
const NON_WORD = /[^0-9A-Za-zÀ-ÖØ-öø-ÿĀ-ž]+/g;
const LETTER = /[A-Za-zÀ-ÖØ-öø-ÿĀ-ž]/;

export function monogramOf(title: string): string {
  const cleaned = title
    .replace(/\.[a-z0-9]{2,4}$/i, '') // dosya uzantısı: "film.mkv"
    .replace(NON_WORD, ' ')
    .trim();
  if (cleaned.length === 0) return '?';

  const words = cleaned.split(' ').filter((w) => w.length > 0);
  if (words.length === 0) return '?';

  const significant = LEADING_ARTICLES.has((words[0] ?? '').toLowerCase()) ? words.slice(1) : words;
  const usable = significant.length > 0 ? significant : words;

  const first = usable[0] ?? '';
  const second = usable[1] ?? '';
  // İkinci kelime harf içermiyorsa (rakam) ondan harf alınmaz.
  if (second !== '' && hasLetter(second)) {
    return `${first[0] ?? ''}${second[0] ?? ''}`.toUpperCase();
  }

  return first.slice(0, 2).toUpperCase();
}

/** Kullanılabilir bir baş harf içeriyor mu? (Rakam değil.) */
function hasLetter(token: string): boolean {
  return LETTER.test(token);
}

/**
 * Kart degrade paleti.
 *
 * Altı çift — hepsi aynı doygunluk/açıklık bandında, böylece ızgara **tek
 * bir doku** gibi görünür. Rastgele seçim değil: `id` üzerinden kararlı seçim.
 */
export const MONOGRAM_PALETTE = [
  { from: '#1e3a8a', to: '#0f1e4d' },
  { from: '#5b21b6', to: '#2e1065' },
  { from: '#0f766e', to: '#052e2b' },
  { from: '#9a3412', to: '#431407' },
  { from: '#7f1d5b', to: '#3b0a2b' },
  { from: '#3f6212', to: '#1a2e05' },
] as const;

/**
 * Bir öğe için kararlı degrade seçer.
 *
 * ## Neden `id` üzerinden?
 *
 * `Math.random()` her render'da çağrılırsa kart kaydırıldıkça renk değişir
 * ve liste "kırpışır". Basit bir string hash'i (FNV-1a) ise aynı öğe için
 * her zaman aynı indeksi verir, sunucudan hiçbir veri gerektirmez.
 *
 * @returns CSS `linear-gradient(...)` değeri.
 */
export function monogramGradient(id: string): string {
  let hash = 0x811c9dc5; // FNV-1a başlangıç ofseti
  for (let i = 0; i < id.length; i += 1) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  const entry = MONOGRAM_PALETTE[hash % MONOGRAM_PALETTE.length] ?? MONOGRAM_PALETTE[0];
  return `linear-gradient(155deg, ${entry.from} 0%, ${entry.to} 100%)`;
}
